// Request for quotation (P2.6, screen 09; 01-SPEC §3 step 3; 08-DOCUMENTS "RFQ"). One RFQ per
// request, sent to each chosen approved supplier separately (no supplier sees another). The
// document holds items, quantities, due date, delivery point and how to reply: never the budget.

import { z } from 'zod';
import type { PrLine, Unit } from '../pr/form';

export const RFQ_CHANNELS = ['email', 'whatsapp', 'print'] as const;
export type RfqChannel = (typeof RFQ_CHANNELS)[number];
// Each supplier costs a few Graph calls; the free plan allows 50 per request.
export const MAX_RFQ_SUPPLIERS = 10;
/** The 3-quote rule (01-SPEC §3); RFQs to fewer are allowed, quotes are checked in P2.7. */
export const QUOTES_EXPECTED = 3;

/** `MED-PR-0312-2026` → `MED-RFQ-0312-2026`: one RFQ per request, so no counter is needed. */
export function rfqNo(prNo: string): string {
  return prNo.replace('-PR-', '-RFQ-');
}

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

export const rfqSendSchema = z.strictObject({
  suppliers: z
    // Any supplier id; the broker checks each one against the approved suppliers (D86).
    .array(z.string().regex(/^SUP-[A-Z0-9-]{1,30}$/, 'Not a supplier id'))
    .min(1, 'Pick at least one supplier')
    .max(MAX_RFQ_SUPPLIERS),
  channel: z.enum(RFQ_CHANNELS),
  dueDate: z.iso.date('Pick a date'),
  note: text(1000).default(''),
});
export type RfqSendInput = z.infer<typeof rfqSendSchema>;

/** Due date: after today and at most 60 days ahead (Riyadh dates, YYYY-MM-DD). */
export function dueDateProblem(due: string, today: string): string | null {
  if (due <= today) return 'Pick a date after today';
  const max = new Date(`${today}T00:00:00Z`);
  max.setUTCDate(max.getUTCDate() + 60);
  return due > max.toISOString().slice(0, 10) ? 'Pick a date within 60 days' : null;
}

/** A sensible default: three working days (Sunday–Thursday) after today. */
export function defaultDueDate(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  for (let added = 0; added < 3;) {
    d.setUTCDate(d.getUTCDate() + 1);
    const day = d.getUTCDay();
    if (day !== 5 && day !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

/** WhatsApp click-to-chat number: digits only, international, or '' when unusable. */
export function waNumber(raw: string): string {
  const digits = raw.replace(/[\s()+-]/g, '').replace(/^00/, '');
  return /^[1-9]\d{6,14}$/.test(digits) ? digits : '';
}

export interface RfqSupplier {
  supplierId: string;
  name: string;
  city: string;
  country: string;
  categories: string[];
  /** Approved for the request's category. */
  inCategory: boolean;
  hasEmail: boolean;
  whatsApp: string;
  /** Last send to this supplier for this RFQ, if any. */
  sent: { channel: RfqChannel; sentAt: string; status: string } | null;
}

export interface RfqDocData {
  rfqNo: string;
  title: string;
  dueDate: string;
  deliverTo: string;
  neededBy: string;
  note: string;
  /** Issuing entity code, for the logo (D116); missing on reminders. */
  entity?: string;
  lines: Pick<PrLine, 'description' | 'specs' | 'qty' | 'unit'>[];
}

export interface RfqPageBody {
  prNo: string;
  status: string;
  category: { id: string; nameEN: string; nameAR: string } | null;
  doc: RfqDocData;
  suppliers: RfqSupplier[];
  canSend: boolean;
  from: string;
}

export interface RfqSentBody {
  rfqNo: string;
  sent: { supplierId: string; emailed: boolean; whatsapp: string }[];
}

type Lang = 'en' | 'ar';

const UNIT_TEXT: Record<Unit, [string, string]> = {
  piece: ['piece', 'قطعة'],
  box: ['box', 'صندوق'],
  pack: ['pack', 'عبوة'],
  set: ['set', 'طقم'],
  kg: ['kg', 'كجم'],
  litre: ['litre', 'لتر'],
  metre: ['metre', 'متر'],
  hour: ['hour', 'ساعة'],
  day: ['day', 'يوم'],
  month: ['month', 'شهر'],
  licence: ['licence', 'ترخيص'],
  lot: ['lot', 'دفعة'],
};

export const unitText = (unit: Unit, lang: Lang) =>
  UNIT_TEXT[unit]?.[lang === 'en' ? 0 : 1] ?? unit;

/** The RFQ wording, shared by the email, the print view and WhatsApp. */
export const RFQ_TEXT = {
  en: {
    heading: 'Request for quotation',
    greeting: 'Dear supplier,',
    intro: (due: string) =>
      `please send your best quotation for the items below by ${due}. Quote unit prices excluding VAT, and state VAT, delivery time and how long your quote is valid.`,
    items: 'Items',
    deliverTo: 'Deliver to',
    neededBy: 'Needed by',
    note: 'Note from SMC Procurement',
    reply: 'Reply to this email with your quotation and quote the reference above.',
    sign: 'SMC Procurement',
  },
  ar: {
    heading: 'طلب عرض سعر',
    greeting: 'عزيزي المورد،',
    intro: (due: string) =>
      `نرجو إرسال أفضل عرض سعر للبنود أدناه قبل ${due}. اذكروا أسعار الوحدات دون ضريبة القيمة المضافة، ومبلغ الضريبة، ومدة التوريد، ومدة صلاحية العرض.`,
    items: 'البنود',
    deliverTo: 'التسليم إلى',
    neededBy: 'مطلوب بحلول',
    note: 'ملاحظة من إدارة المشتريات',
    reply: 'يرجى الرد على هذا البريد بعرض السعر مع ذكر الرقم المرجعي أعلاه.',
    sign: 'إدارة المشتريات في SMC',
  },
} as const;

/** Dates in RFQs are calendar dates: 20 Oct 2026 / ٢٠ أكتوبر ٢٠٢٦ is avoided for digits. */
export function rfqDate(iso: string, lang: Lang): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`));
}

/** Plain text for WhatsApp (both languages). */
export function rfqPlainText(doc: RfqDocData): string {
  const part = (lang: Lang) => {
    const t = RFQ_TEXT[lang];
    const lines = doc.lines.map(
      (l) =>
        `• ${l.qty} × ${unitText(l.unit, lang)} · ${l.description}${l.specs ? ` (${l.specs})` : ''}`,
    );
    return [
      `${t.heading} · ${doc.rfqNo}`,
      `${t.greeting} ${t.intro(rfqDate(doc.dueDate, lang))}`,
      ...lines,
      `${t.deliverTo}: ${doc.deliverTo}${doc.neededBy ? ` · ${t.neededBy} ${rfqDate(doc.neededBy, lang)}` : ''}`,
      ...(doc.note ? [`${t.note}: ${doc.note}`] : []),
      t.sign,
    ].join('\n');
  };
  return `${part('en')}\n\n${part('ar')}`;
}
